import { AI_AGREEMENT_URL } from '@/consts/aiAgreement';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useSubmitPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useSubmitPayeeInfo';
import {
  AiAgentSettingsData,
  AiAgentSettingsForm,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Caption,
  Form,
  FormCheckbox,
  TextLink,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import styles from '../../styles.module.scss';
import { HeaderSection, SingleRow } from '../Common';

const cx = classnames.bind(styles);

export type Props = {
  data?: AiAgentSettingsData | undefined;
};

export const Edit: FC<Props> = memo(({ data }) => {
  const { onEditFormDirtyChange, handleCancelEdit } = usePayeeDetailDrawer();
  const { t } = useTranslation();

  const invoiceDownloadAgent = useMemo(() => {
    return data?.ai_agent_service_settings?.find(
      (setting) => setting.service_type === 'invoice_download_agent'
    );
  }, [data?.ai_agent_service_settings]);

  const defaultValues: AiAgentSettingsForm = useMemo(
    () => ({
      invoice_download_agent_enabled: invoiceDownloadAgent?.is_enabled ?? false,
    }),
    [invoiceDownloadAgent?.is_enabled]
  );

  const methods = useForm<AiAgentSettingsForm>({
    defaultValues,
    mode: 'all',
  });

  const { isValid, isDirty, isSubmitting } = useFormState({
    control: methods.control,
  });

  const { onSubmit, isLoading } = useSubmitPayeeInfo<AiAgentSettingsForm>({
    apPayeeId: data?.id ?? '',
  });

  useEffect(() => {
    if (!isSubmitting) return;
    onEditFormDirtyChange?.(false);
  }, [isSubmitting, onEditFormDirtyChange]);

  useEffect(() => {
    onEditFormDirtyChange?.(isDirty);
  }, [isDirty, onEditFormDirtyChange]);

  const handleCancel = useCallback(() => {
    handleCancelEdit?.(isDirty);
  }, [handleCancelEdit, isDirty]);

  return (
    <Form<AiAgentSettingsForm>
      formMethod={methods}
      onSubmit={onSubmit}
      testId='ai-agent-settings-edit'
      className={cx('sectionContainer')}
    >
      <HeaderSection
        displayName={t('ai_agents_setting')}
        actionButton={
          <ButtonGroup direction='horizontal'>
            <ButtonV2
              color='primary'
              isSecondary
              onClick={handleCancel}
              type='button'
              isDisabled={isLoading}
              testId='cancel-button'
              containerClassName={cx('cancelButton')}
            >
              {t('cancel')}
            </ButtonV2>
            <ButtonV2
              color='primary'
              size='xs'
              type='submit'
              isTransparent={isLoading}
              isLoading={isLoading}
              isDisabled={!isValid || isLoading}
              testId='save-button'
              buttonContainerClassName={cx('saveButton')}
            >
              {t('save')}
            </ButtonV2>
          </ButtonGroup>
        }
      />
      <div className={cx('bodySectionContainer')}>
        <SingleRow
          label={t('ai_agents_setting_invoice_download_service_title')}
          renderContent={
            <div>
              <FormCheckbox
                name='invoice_download_agent_enabled'
                control={methods.control}
                inputId='invoice-download-agent-enabled'
                label={t('use')}
              />
              <Caption type='alert' textClassName={cx('alertCaption')}>
                <TextLink href={AI_AGREEMENT_URL} target='_blank'>
                  {t(
                    'ai_agents_setting_main_setting_confirm_enable_start_service_terms_link'
                  )}
                </TextLink>
                <span>
                  {t(
                    'ai_agents_setting_payee_detail_terms_confirmation_suffix'
                  )}
                </span>
              </Caption>
            </div>
          }
        />
      </div>
    </Form>
  );
});

Edit.displayName = 'AiAgentSettingsEdit';
